import React, { useEffect, useState, useCallback } from "react";
import AdminPageLayout from "../components/AdminPageLayout";
import { useToast } from "../../common/toast/ToastContext";
import {
  fetchPracticeSets, fetchPracticeSetById,
  createPracticeSet, updatePracticeSet, deletePracticeSet,
} from "./adminContentService";

const STATUSES = ["DRAFT", "ACTIVE", "INACTIVE"];
const PRACTICE_TYPES = ["FILL_BLANK", "REORDER_SENTENCE", "SPEAKING", "WRITING", "MATCHING"];
const emptyItem = () => ({ question: "", hint: "", answer: "", sortOrder: 0 });
const emptyForm = { title: "", instructions: "", practiceType: "FILL_BLANK", status: "DRAFT", items: [emptyItem()] };

const AdminPracticeSetsPage = () => {
  const { showToast } = useToast();
  const [view, setView] = useState("list");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const loadItems = useCallback(async () => {
    setLoading(true);
    const params = { page, size: 20 };
    if (search) params.search = search;
    if (statusFilter) params.status = statusFilter;
    const res = await fetchPracticeSets(params);
    if (res.status === 200) {
      const d = res.data;
      setItems(Array.isArray(d) ? d : d.content ?? []);
      setTotalPages(Array.isArray(d) ? 1 : d.totalPages ?? 1);
    }
    setLoading(false);
  }, [page, search, statusFilter]);

  useEffect(() => { if (view === "list") loadItems(); }, [view, loadItems]);

  const openCreate = () => { setEditingId(null); setForm(emptyForm); setView("form"); };

  const openEdit = async (id) => {
    const res = await fetchPracticeSetById(id);
    if (res.status === 200) {
      const d = res.data;
      setForm({
        title: d.title || "",
        instructions: d.instructions || "",
        practiceType: d.practiceType || "FILL_BLANK",
        status: d.status || "DRAFT",
        items: d.items?.length ? d.items : [emptyItem()],
      });
      setEditingId(id);
      setView("form");
    } else {
      showToast("Failed to load practice set", "error");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this practice set?")) return;
    const res = await deletePracticeSet(id);
    if (res.status === 200 || res.status === 204) { showToast("Deleted", "success"); loadItems(); }
    else showToast(res.message || "Delete failed", "error");
  };

  const handleSave = async () => {
    if (!form.title.trim()) { showToast("Title is required", "error"); return; }
    setSaving(true);
    const payload = {
      title: form.title.trim(),
      instructions: form.instructions,
      practiceType: form.practiceType,
      status: form.status,
      items: form.items.filter((it) => it.question.trim()).map((it, i) => ({ ...it, sortOrder: i })),
    };
    const res = editingId ? await updatePracticeSet(editingId, payload) : await createPracticeSet(payload);
    setSaving(false);
    if (res.status === 200 || res.status === 201) {
      showToast(editingId ? "Updated" : "Created", "success");
      setView("list");
    } else {
      showToast(res.message || "Save failed", "error");
    }
  };

  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState("");

  const setField = (key, val) => setForm((f) => ({ ...f, [key]: val }));
  const updateItem = (idx, field, val) =>
    setField("items", form.items.map((it, i) => i === idx ? { ...it, [field]: val } : it));
  const addItem = () => setField("items", [...form.items, emptyItem()]);
  const removeItem = (idx) => setField("items", form.items.filter((_, i) => i !== idx));

  // Bulk format: each line = "Question | Hint | Answer"  or  "Question || Answer"  or just "Question"
  const handleBulkAdd = () => {
    const lines = bulkText.split("\n").map((l) => l.trim()).filter(Boolean);
    if (!lines.length) return;
    const parsed = lines.map((line) => {
      const parts = line.split("|").map((p) => p.trim());
      if (parts.length >= 3) return { question: parts[0], hint: parts[1], answer: parts[2], sortOrder: 0 };
      if (parts.length === 2) return { question: parts[0], hint: "", answer: parts[1], sortOrder: 0 };
      return { question: parts[0], hint: "", answer: "", sortOrder: 0 };
    });
    setField("items", [...form.items.filter((it) => it.question.trim()), ...parsed]);
    setBulkText("");
    setBulkOpen(false);
  };

  const inp = { width: "100%", padding: "8px 12px", borderRadius: 6, border: "1px solid #ccc", boxSizing: "border-box" };

  if (view === "form") {
    return (
      <div className="admin-container">
        <div className="admin-header">
          <h2>{editingId ? "Edit Practice Set" : "New Practice Set"}</h2>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={() => setView("list")} style={{ padding: "8px 16px", borderRadius: 6, border: "1px solid #ccc", background: "#fff", cursor: "pointer" }}>Cancel</button>
            <button onClick={handleSave} disabled={saving} style={{ padding: "8px 20px", background: "#019095", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer" }}>
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 16, maxWidth: 700 }}>
          <div>
            <label style={{ fontSize: 13, fontWeight: 600 }}>Title *</label>
            <input style={inp} value={form.title} onChange={(e) => setField("title", e.target.value)} placeholder="Practice set title" />
          </div>
          <div>
            <label style={{ fontSize: 13, fontWeight: 600 }}>Instructions</label>
            <textarea style={{ ...inp, minHeight: 80, resize: "vertical" }} value={form.instructions} onChange={(e) => setField("instructions", e.target.value)} placeholder="Instructions for the student…" />
          </div>
          <div style={{ display: "flex", gap: 12 }}>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 13, fontWeight: 600 }}>Practice Type</label>
              <select style={inp} value={form.practiceType} onChange={(e) => setField("practiceType", e.target.value)}>
                {PRACTICE_TYPES.map((t) => <option key={t}>{t}</option>)}
              </select>
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 13, fontWeight: 600 }}>Status</label>
              <select style={inp} value={form.status} onChange={(e) => setField("status", e.target.value)}>
                {STATUSES.map((s) => <option key={s}>{s}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 8 }}>Items</label>
            {form.items.map((it, i) => (
              <div key={i} style={{ background: "#f9f9f9", borderRadius: 8, padding: 12, marginBottom: 10 }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                  <span style={{ fontWeight: 600, fontSize: 13 }}>Item {i + 1}</span>
                  <button onClick={() => removeItem(i)} style={{ color: "#e53935", background: "none", border: "none", cursor: "pointer" }}>Remove</button>
                </div>
                <input style={{ ...inp, marginBottom: 8 }} value={it.question} onChange={(e) => updateItem(i, "question", e.target.value)} placeholder="Question *" />
                <input style={{ ...inp, marginBottom: 8 }} value={it.hint} onChange={(e) => updateItem(i, "hint", e.target.value)} placeholder="Hint (optional)" />
                <input style={inp} value={it.answer} onChange={(e) => updateItem(i, "answer", e.target.value)} placeholder="Answer" />
              </div>
            ))}
            <button onClick={addItem} style={{ padding: "6px 14px", borderRadius: 6, border: "1px dashed #019095", color: "#019095", background: "none", cursor: "pointer" }}>+ Add Item</button>

            {/* Bulk Add */}
            <div style={{ marginTop: 12 }}>
              <button
                onClick={() => setBulkOpen((o) => !o)}
                style={{ padding: "6px 14px", borderRadius: 6, border: "1px dashed #7c3aed", color: "#7c3aed", background: bulkOpen ? "#f5f3ff" : "none", cursor: "pointer", fontSize: 13, fontWeight: 600 }}
              >
                {bulkOpen ? "▲ Close Bulk Add" : "⚡ Bulk Add Items"}
              </button>

              {bulkOpen && (
                <div style={{ marginTop: 10, background: "#f5f3ff", border: "1px solid #c4b5fd", borderRadius: 8, padding: 14 }}>
                  <p style={{ margin: "0 0 6px", fontSize: 13, color: "#555" }}>
                    One item per line. Format: <code style={{ background: "#ede9fe", padding: "1px 6px", borderRadius: 4 }}>Question | Hint | Answer</code>
                    <br />
                    <span style={{ color: "#888" }}>Hint is optional: <code style={{ background: "#ede9fe", padding: "1px 6px", borderRadius: 4 }}>Question | Answer</code> also works.</span>
                  </p>
                  <textarea
                    autoFocus
                    value={bulkText}
                    onChange={(e) => setBulkText(e.target.value)}
                    placeholder={`The ___ is on the table. | Think of furniture | book\nShe ___ to school every day. | | walks\nWhat is the capital of France? | It's in Europe | Paris`}
                    style={{ width: "100%", minHeight: 140, padding: "10px 12px", borderRadius: 6, border: "1px solid #c4b5fd", fontSize: 13, resize: "vertical", boxSizing: "border-box", fontFamily: "monospace", lineHeight: 1.6 }}
                  />
                  <div style={{ display: "flex", gap: 8, marginTop: 8, alignItems: "center" }}>
                    <button
                      onClick={handleBulkAdd}
                      disabled={!bulkText.trim()}
                      style={{ padding: "7px 20px", background: "#7c3aed", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontWeight: 600, fontSize: 13, opacity: bulkText.trim() ? 1 : 0.5 }}
                    >
                      Add {bulkText.split("\n").filter((l) => l.trim()).length || 0} items
                    </button>
                    <button onClick={() => { setBulkOpen(false); setBulkText(""); }}
                      style={{ padding: "7px 14px", border: "1px solid #ccc", background: "#fff", borderRadius: 6, cursor: "pointer", fontSize: 13 }}>
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <AdminPageLayout
      title="Practice Sets"
      subtitle="Manage practice sets linked to topics."
      actions={<button className="adm-btn adm-btn-primary" onClick={openCreate}>+ New Practice Set</button>}
    >
      <div style={{ display: "flex", gap: 10, marginBottom: 20, flexWrap: "wrap" }}>
        <input placeholder="Search…" value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }}
          style={{ flex: 1, minWidth: 180, padding: "8px 12px", borderRadius: 6, border: "1px solid #ccc" }} />
        <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
          style={{ padding: "8px 12px", borderRadius: 6, border: "1px solid #ccc" }}>
          <option value="">All Status</option>
          {STATUSES.map((s) => <option key={s}>{s}</option>)}
        </select>
      </div>

      {loading ? <p className="adm-loading">Loading…</p> : (
        <table className="announcement-table">
          <thead><tr><th>Title</th><th>Type</th><th>Status</th><th>Items</th><th>Actions</th></tr></thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan="5">No practice sets found.</td></tr>
            ) : items.map((item) => (
              <tr key={item.id}>
                <td style={{ fontWeight: 600 }}>{item.title}</td>
                <td style={{ fontSize: 12 }}>{item.practiceType}</td>
                <td><span style={{ padding: "2px 8px", borderRadius: 999, fontSize: 12, background: item.status === "ACTIVE" ? "#e8f5e9" : item.status === "DRAFT" ? "#fff8e1" : "#fce4ec", color: item.status === "ACTIVE" ? "#2e7d32" : item.status === "DRAFT" ? "#f57f17" : "#c62828" }}>{item.status}</span></td>
                <td style={{ textAlign: "center" }}>{item.items?.length || 0}</td>
                <td>
                  <button onClick={() => openEdit(item.id)}>Edit</button>
                  <button className="delete-btn" onClick={() => handleDelete(item.id)}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {totalPages > 1 && (
        <div style={{ marginTop: 16 }}>
          <button disabled={page === 0} onClick={() => setPage(page - 1)}>Prev</button>
          <span style={{ margin: "0 10px" }}>Page {page + 1} / {totalPages}</span>
          <button disabled={page + 1 >= totalPages} onClick={() => setPage(page + 1)}>Next</button>
        </div>
      )}
    </AdminPageLayout>
  );
};

export default AdminPracticeSetsPage;
